import * as React from "react";
import {ReactElement, useEffect, useState} from "react";
import Plan from "../../../model/Plan";
import Model from "../../../model/Model";
import PlanChange, {PlanChangeType} from "../../../model/PlanChange";
import {
  Box,
  Checkbox,
  DialogContentText,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  Tooltip,
  useTheme
} from "@mui/material";
import {useResources} from "../../../stores/ResourceProvider";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import Asset, {AssetCategory, AssetType} from "../../../model/Asset";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {AddCircleRounded} from "@mui/icons-material";
import {CreatePlanChangeInput, UpdatePlanChangeInput} from "../../../API";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import IconicButton from "../../../components/controls/IconicButton";
import AssetConversion, {AssetConversionType} from "../../../model/AssetConversion";
import {
  createUUID,
  getISODateTime,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import {IncomeType} from "../../../model/Income";
import Visible from "../../../components/Visible";
import Tracking from "../../../components/Tracking";
import QCDStrategyChange from "../../../model/changes/QCDStrategyChange";
import ChatbotOpenImg from "../../../images/ChatbotOpen.png";
import ChatbotCloseImg from "../../../images/Chatbot.png";
import {addMonths} from "date-fns";

class QCDEditDetail {
  srcAsset?: Asset
  year: string
  isAmount: boolean
  amount: string
  percent: string

  constructor(data: any) {
    this.srcAsset = data.srcAsset ?? data.iraAsset
    this.year = data.year ? data.year.toString() : ""
    this.isAmount = data.isAmount !== undefined ? data.isAmount : true
    this.amount = data.amount ?? "$0"
    this.percent = data.percent ?? "100"
  }
}

const QCDStrategyDialog = ({
  open,
  plan,
  model,
  onClose,
  onSave
}: {
  open? : boolean
  plan: Plan
  model: Model
  onClose?(): void
  onSave?(plan: Plan, update: PlanChange): void
}) => {
  const [planModel, setPlanModel] = useState<Plan>(plan)
  const [isOpen, setIsOpen] = useState<boolean>(open === true)
  const [planChange, setPlanChange] = useState<QCDStrategyChange | undefined>()
  const [editConversions, setEditConversions] = useState<QCDEditDetail[]>([])
  const [srcAssets, setSrcAssets] = useState<Asset[]>([])
  const [commitChanges, setCommitChanges] = useState<boolean>(false)
  const [showHelp, setShowHelp] = useState<boolean>(false)

  const theme = useTheme()
  const styles = {
    itemBox: {
      display:"flex",
      flexGrow:1,
      alignItems:"center",
      // width: "100%",
      backgroundColor: theme.palette.grey["50"],
      borderWidth: 1,
      borderColor: theme.palette.background.default,
      borderStyle: "solid",
      borderRadius: 0,
      padding: 1,
      marginBottom: 1,
      boxShadow: "rgba(0, 0, 0, 0.15) 0px 3px 3px 0px"
    },
    autoField: {
      marginTop: "8px"
    },
    radio: {
      paddingTop: 0,
      paddingLeft: 0,
      paddingRight: "4px"
    },
    helpText: {
      fontSize: 14,
      lineHeight: 1.4
    }  }

  const {modelStore, notify, confirm} = useResources()

  useEffect(() => {
    setPlanModel(plan)
    setIsOpen(open === true)
    let change: QCDStrategyChange = plan.getChange(PlanChangeType.QCDStrategy) as QCDStrategyChange
    if (!change) {
      change = new QCDStrategyChange({
        accountId: plan.accountId,
        userId: model.userId,
        modelId: model.id,
        planId: plan.id,
        changeType: PlanChangeType.QCDStrategy,
        name: "QCD Strategy",
        description: "",
        enabled: true,
        conversions: []
      })
    }
    setPlanChange(change)
    // let srcs = model.assets.filter((a: Asset) => a.assetTypeDef.assetType === AssetType.TraditionalIRA)
    let srcs = model.assets.filter((a: Asset) => a.assetCategory === AssetCategory.LiquidInvestableAssets && a.assetTypeDef.isTraditionalIRA)
    setSrcAssets(srcs)

    const editDetails: QCDEditDetail[] = []
    if (change.conversions && change.conversions.length > 0) {
      change.conversions.forEach((c: AssetConversion) => {
        const srcAsset = srcs.find((a: Asset) => a.id === c.srcAssetId)
        if (srcAsset) {
          editDetails.push(new QCDEditDetail({
            srcAsset: srcAsset,
            year: String(c.year),
            isAmount: !(c.percent > 0),
            amount: numberToMoneyFormat(c.amount, 0) ?? "",
            percent: (c.percent > 0) ? numberToPercentFormat(c.percent * 100, 0, 'decimal') : "100",
          }))
        }
      })
    } else {
      // Add an empty conversion by default
      editDetails.push(new QCDEditDetail({}))
    }
    setEditConversions(editDetails)
  }, [plan, open])

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    if (commitChanges) {
      confirm.show('Confirm Commit', `Commit will move QCDs from this QCD Strategy to your model's Asset Conversions section after you are done experimenting.`,
        ['Confirm', 'Cancel'],
        async () => {
          await saveChanges()
          return true
        })
    } else {
      await saveChanges()
    }
  }

  const saveChanges = async() => {
    try {
      let change: PlanChange | undefined

      const details: AssetConversion[] = []
      editConversions.forEach((conv: QCDEditDetail) => {
        if (conv.srcAsset && conv.year && conv.amount) {
          details.push(new AssetConversion({
            id: createUUID(),
            createdAt: getISODateTime(),
            accountId: plan.accountId,
            userId: plan.userId,
            modelId: plan.modelId,
            description: `${conv.year} ${conv.srcAsset.description} QCD`,
            conversionType: AssetConversionType.QCD,
            srcAssetId: conv.srcAsset.id,
            dstAssetId: "",
            year: parseInt(conv.year),
            amount: conv.isAmount ? moneyToNumberFormat(conv.amount, 0) : 0,
            percent: !conv.isAmount ? parseInt(conv.percent) / 100 ?? 0 : 0,
            nonTaxableAmount: 0,
            incomeType: IncomeType.None
          }))
        }
      })
      details.sort((a: AssetConversion, b: AssetConversion) => a.year - b.year)

      if (planChange && !planChange.id) {
        const input: CreatePlanChangeInput = {
          accountId: planChange?.accountId,
          userId: planChange.userId,
          modelId: planChange.modelId,
          planId: planChange.planId,
          changeType: planChange.changeType,
          name: planChange.name,
          description: generateDescription(),
          enabled: !commitChanges,
          details: JSON.stringify(details)
        }
        change = await modelStore.createPlanChange(input)
        Tracking.event({action: "Conversion Strategy Created"})
      } else if (planChange) {
        const input: UpdatePlanChangeInput = {
          id: planChange.id,
          description: generateDescription(),
          enabled: !commitChanges,
          details: JSON.stringify(details)
        }
        change = await modelStore.updatePlanChange(input)
        Tracking.event({action: "Conversion Strategy Updated"})
      }

      if (change && commitChanges) {
        await change.commit(model, modelStore)
        const input: UpdatePlanChangeInput = {
          id: change.id,
          description: "",
          enabled: false,
          details: JSON.stringify([])
        }
        change = await modelStore.updatePlanChange(input)
      }

      if (onSave && change) {
        onSave(plan, change)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const generateDescription = () => {
    return `${editConversions.length} Conversions`
  }

    const handleAdd = () => {
    setEditConversions([...editConversions, new QCDEditDetail({})])
  }

  const handleDelete = (index: number) => {
    editConversions.splice(index, 1)
    setEditConversions([...editConversions])
  }

  if (!planChange) {
    return null
  }

  const getMinYear = (conv: QCDEditDetail): number => {
    let year = (new Date()).getFullYear()
    if (conv.srcAsset) {
      if (conv.srcAsset.owner) {
        let minDate = addMonths(conv.srcAsset.owner.getDateOnAge(70), 6)
        year = Math.max(minDate.getFullYear(), year)
      }
    }
    return year
  }

  // const minYear = (new Date()).getFullYear()

  const incomeTypes: IncomeType[] = [
    IncomeType.None,
    IncomeType.WagesSalariesTips,
    IncomeType.LongTermCapitalGainsOrLoss,
    IncomeType.ShortTermCapitalGainsOrLoss,
    IncomeType.TaxableIRADistribution,
    IncomeType.OtherIncome
  ]

  const renderAmountLabel = (conv: QCDEditDetail) => {
    return (
      <Box display="inline-block">
        <Radio checked={conv.isAmount} sx={styles.radio}
               onChange={(event: any) => {
                 conv.isAmount = event.target.checked
                 setEditConversions([...editConversions])
               }}/>
        <span style={{marginRight: "16px"}}>Amount</span>
        <Radio checked={!conv.isAmount} sx={styles.radio} onChange={(event: any) => {
          conv.isAmount = !event.target.checked
          setEditConversions([...editConversions])
        }}/>
        <span>% of RMD</span>
      </Box>
    )
  }

  const renderHelp = () => {
    return (
      <Box sx={{display:"flex", flexDirection:"column", flexGrow:1, ml:1,  bgcolor:"#f1f1f2", borderBottomLeftRadius:20, borderTopRightRadius:20, borderBottomRightRadius:20, padding:2}}>
        <p style={styles.helpText}>A <b>Qualified Charitable Distribution (QCD)</b> is a direct transfer of funds from an <b>IRA (Individual Retirement Account)</b> to a <b>qualified 501(c)(3) charity</b>. It allows <b>individuals aged 70½ or older</b> to donate up to <b>$100,000 per year</b> tax-free.</p>
        <p style={styles.helpText}>Key benefits and rules:</p>
        <ul style={styles.helpText}>
          <li>The <b>donation is excluded from taxable income</b>, helping reduce overall tax liability.</li>
          <li><b>Counts toward Required Minimum Distributions (RMDs)</b> for individuals 73 and older.</li>
          <li>Must be made directly from an <b>IRA (not a 401(k) or other retirement account)</b>.</li>
          <li>The recipient must be a <b>qualified charity</b> (private foundations and donor-advised funds do not qualify).</li>
          <li>The donor <b>cannot claim a charitable deduction</b> for the contribution.</li>
        </ul>
        <p style={styles.helpText}>QCDs are a tax-efficient way for retirees to support charitable causes while managing their taxable income.</p>
      </Box>
    )
  }

  const commitDisabled = editConversions.length === 0

  return (
    <ModelEditDialog title="QCD Strategy" open={isOpen}
                     size={showHelp ? "lg" : "sm"}
                     onCancel={handleClose}
                     onSave={handleSave}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={showHelp ? 6 : 12}>
          <DialogContentText>
            <Box display="flex" justifyContent="stretch" alignItems="flex-start">
              <Box display="flex" flexGrow={1} pr={1}>
                Schedule future Qualified Charitable Distributions. Owner must be 70½ the year of the distribution and amount maximum is $100,000.
              </Box>
              <Box display="flex" flexGrow={0} justifyContent="flex-end" width={30} pl={1}>
                <IconButton size="small" sx={{padding:0}}
                            onClick={(event: any) => {
                              setShowHelp(!showHelp)
                            }}
                >
                  <Tooltip title="Ask Finn">
                    <img src={showHelp ? ChatbotOpenImg : ChatbotCloseImg} alt="chatbot"
                         height={30} width={30} style={{borderRadius:"15px"}}/>
                  </Tooltip>
                </IconButton>
              </Box>
            </Box>
          </DialogContentText>
          {editConversions.map((conv: QCDEditDetail, index: number) =>
            <Box sx={styles.itemBox}>
              <Grid container spacing={1}>
                <Grid item xs={5} sx={styles.autoField}>
                  <TextFieldValidator
                    type="text"
                    validators={{ required: true }}
                    // required
                    name="srcAsset"
                    label="Source Asset"
                    variant="standard"
                    autocompleteOptions={{
                      freeSolo: false,
                      options: srcAssets,
                      getOptionLabel: (option: Asset) => option ? option.description : "None",
                      isOptionEqualToValue: (option: Asset, value: Asset) => option.id === value.id,
                      value: conv.srcAsset || null,
                      onChange: (event: any, value: Asset, reason: any) => {
                        const conv = editConversions[index]
                        conv.srcAsset = value
                        if (value) {
                          conv.amount = numberToMoneyFormat(Math.min(value.balance, AssetConversion.MaxQCD), 0)
                        } else {
                          conv.amount = "$0"
                        }
                        setEditConversions([...editConversions])
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextFieldValidator
                    type="number"
                    validators={{ required: true, isInt: true, minValue: getMinYear(conv) }}
                    name="year"
                    variant="standard"
                    margin="dense"
                    label="Year"
                    value={conv.year}
                    onChange={(event: any) => {
                      editConversions[index].year = event.target.value
                      setEditConversions([...editConversions])
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Box display="flex" justifyContent="space-between">
                    <Visible cond={conv.isAmount}>
                      <TextFieldValidator
                        type="string"
                        validators={{ required: conv.isAmount, isMoney: true, minMoney: 0, maxMoney: AssetConversion.MaxQCD }}
                        name="amount"
                        variant="standard"
                        margin="dense"
                        label={renderAmountLabel(conv)}
                        value={conv.amount}
                        onChange={(event: any) => {
                          const value = event.target.value === "" ? "$0" : event.target.value
                          editConversions[index].amount = value
                          setEditConversions([...editConversions])
                        }}
                      />
                    </Visible>
                    <Visible cond={!conv.isAmount}>
                      <TextFieldValidator
                        type="string"
                        validators={{ required: !conv.isAmount, isInt: true, minValue: 0, maxValue: 100 }}
                        name="percent"
                        variant="standard"
                        margin="dense"
                        label={renderAmountLabel(conv)}
                        value={conv.percent}
                        onChange={(event: any) => {
                          const value = event.target.value === "" ? "0" : event.target.value
                          editConversions[index].percent = value
                          setEditConversions([...editConversions])
                        }}
                      />
                    </Visible>
                  </Box>
                </Grid>
                <Grid item xs={1} sx={{display:"flex", justifyContent:"flex-end", alignItems:"center"}}>
                  <IconicButton
                    onClick={() => handleDelete(index)}>
                    <DeleteIcon/>
                  </IconicButton>
                </Grid>
              </Grid>
            </Box>
          )}
          <Box display="flex" justifyContent="center">
            <IconButton sx={{height:28, width:28}} onClick={handleAdd}>
              <AddCircleRounded sx={{color: theme.palette.secondary.light}}/>
            </IconButton>
          </Box>
          <Box display="flex">
            <FormControlLabel control={
              <Checkbox checked={commitChanges}
                        disabled={commitDisabled}
                        onChange={(event: any) => {
                          setCommitChanges(event.target.checked)
                        }}
              />}
              label={<span>Commit these QCDs to {model.name}.</span>}
              sx={{marginTop:1}}
              componentsProps={{
                typography: {
                  variant:"body2"
                }}
              }
            />
          </Box>
        </Grid>
        {showHelp &&
          <Grid item xs={12} sm={showHelp ? 6 : 12}>
            {renderHelp()}
          </Grid>
        }
      </Grid>
    </ModelEditDialog>
  )
}

export default QCDStrategyDialog